// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { auth, db } from '../firebase-config';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signOut,
  updateProfile,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// Detect if running inside Capacitor native app (Play Store build)
const isNativeApp = () => {
  return typeof window !== 'undefined' &&
    window.Capacitor !== undefined &&
    window.Capacitor.isNativePlatform();
};

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [customUserData, setCustomUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBusiness, setIsBusiness] = useState(false);

  const combinedCurrentUser = useMemo(() => {
    if (!firebaseUser) return null;
    return { ...firebaseUser, profile: customUserData };
  }, [firebaseUser, customUserData]);

  // Set persistence to LOCAL on app start — this prevents sessionStorage loss
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(console.error);
  }, []);

  const loadFirestoreUser = async (user) => {
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);
    let data = {};

    if (userDoc.exists()) {
      data = userDoc.data();
      setIsAdmin(!!data.admin);
      setIsBusiness(!!data.admin || !!data.isBusiness);
    } else {
      data = {
        email: user.email,
        name: user.displayName || 'Unnamed User',
        photoURL: user.photoURL || null,
        createdAt: new Date(),
        admin: false,
        isBusiness: false,
        gender: null,
        age: null,
        dob: null,
        phoneNumber: null,
        walletBalance: 0,
        coins: 0,
      };
      await setDoc(userDocRef, data);
      setIsAdmin(false);
      setIsBusiness(false);
    }
    setCustomUserData(data);
    return data;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setFirebaseUser(user);
        await loadFirestoreUser(user);
      } else {
        setFirebaseUser(null);
        setCustomUserData(null);
        setIsAdmin(false);
        setIsBusiness(false);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // NEW: Upgrade to Business Account
  const upgradeToBusiness = async () => {
    if (!firebaseUser) return;
    const userRef = doc(db, 'users', firebaseUser.uid);
    try {
      await updateDoc(userRef, { isBusiness: true });
      setIsBusiness(true);
      setCustomUserData(prev => ({ ...prev, isBusiness: true }));
      return true;
    } catch (error) {
      console.error("Error upgrading account:", error);
      throw error;
    }
  };

  const signup = async (email, password, name, phoneNumber, gender, age, dob, location) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    await updateProfile(user, { displayName: name });

    const newCustomData = {
      email: user.email,
      name,
      phoneNumber,
      gender,
      age,
      dob,
      location,
      photoURL: null,
      createdAt: new Date(),
      admin: false,
      isBusiness: false,
      walletBalance: 0,
      coins: 0
    };

    await setDoc(doc(db, 'users', user.uid), newCustomData);
    setFirebaseUser(user);
    setCustomUserData(newCustomData);
    return userCredential;
  };

  const login = async (email, password) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    await loadFirestoreUser(userCredential.user);
    setFirebaseUser(userCredential.user);
    return userCredential;
  };

  // ── Google Login ─────────────────────────────────────────────────
  // Native app (Play Store): uses @codetrix-studio/capacitor-google-auth
  //   → opens native Google sign-in sheet, no browser/popup problems
  // Browser/PWA: uses signInWithPopup (works fine outside native webview)
  const loginWithGoogle = async () => {
    let result;
 
    if (isNativeApp()) {
      try {
        // Requires: npm install @codetrix-studio/capacitor-google-auth
        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
 
        GoogleAuth.initialize({
          clientId: '753931936573-lld1tok36sa0ga25dc9gnrt6ctfb7fkg.apps.googleusercontent.com', // ← Replace this
          scopes: ['profile', 'email'],
          grantOfflineAccess: true,
        });
 
        const googleUser = await GoogleAuth.signIn();
        const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
        result = await signInWithCredential(auth, credential);
 
      } catch (error) {
        console.error("Native Google Sign-In error:", error);
        throw new Error('Google sign-in failed. Please try again.');
      }
 
    } else {
      // Browser / PWA path
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
 
      try {
        result = await signInWithPopup(auth, provider);
      } catch (error) {
        console.error("Google Popup error:", error.code, error.message);
        throw error;
      }
    }
 
    await loadFirestoreUser(result.user);
    setFirebaseUser(result.user);
    return result;
  };

  const logout = () => {
    setFirebaseUser(null);
    setCustomUserData(null);
    setIsAdmin(false);
    setIsBusiness(false);
    localStorage.removeItem('profile_completed_session');
    return signOut(auth);
  };

  const value = {
    firebaseUser,
    currentUser: combinedCurrentUser,
    isAdmin,
    isBusiness,
    signup,
    login,
    loginWithGoogle,
    logout,
    upgradeToBusiness
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}